import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useSelectionSidebarLoading } from "../../Bundle/useSelectionSidebarLoadingState";
import { LayerTypeLayerSummaryProps, SupportedRegions } from "../../types";
import { Alert } from "react-migration/components/Alert";
import { useDesignationAreaByGeometry } from "react-migration/domains/constraints/designation/hooks/useDesignationAreaByGeometry";
import { KeyIndicatorColor, useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";
import { DesignationAreaChart } from "react-migration/domains/constraints/designation/components/DesignationAreaChart";

const formatter = new Intl.NumberFormat(undefined, {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CROP_OVERLAP = true;

export function HazardWetlandsLayerSummary({ selection, onLoadingChange }: LayerTypeLayerSummaryProps) {
  const { data, loading, error } = useDesignationAreaByGeometry(
    selection?.feature?.geometry,
    [
      "us_wetlands_estuarine_and_marine_deepwater",
      "us_wetlands_estuarine_and_marine_wetland",
      "us_wetlands_freshwater_emergent_wetland",
      "us_wetlands_freshwater_forested_shrub_wetland",
      "us_wetlands_freshwater_pond",
      "us_wetlands_lake",
      "us_wetlands_riverine",
      "us_wetlands_other",
      "us_wetlands",
    ],
    CROP_OVERLAP
  );

  useKeyIndicatorTags(() => {
    if (!data) {
      return [];
    }

    const wetlandCategories = data.filter((d) => d.sub_category !== "us_wetlands");

    if (!data.length || wetlandCategories.length === 0) {
      return [{ tag: "Not impacted", color: KeyIndicatorColor.GREEN }];
    }

    const totalWetlandsCategory = wetlandCategories.reduce((acc, d) => {
      return acc + d.category_coverage_percentage;
    }, 0);

    const totalCoveragePercentage = Math.min(100, totalWetlandsCategory);

    const getColor = (coverage: number) => {
      if (coverage < 5) {
        return KeyIndicatorColor.GREEN;
      }

      if (coverage < 50) {
        return KeyIndicatorColor.ORANGE;
      }

      return KeyIndicatorColor.RED;
    };

    return [
      {
        tag: formatter.format(totalCoveragePercentage / 100),
        color: getColor(totalCoveragePercentage),
      },
    ];
  }, [data]);

  useSelectionSidebarLoading(onLoadingChange, loading);

  if (loading) {
    return (
      <div className="relative p-2 h-[168px]">
        <SkeletonLoading rows={2} showPadding={false} bgClassName="bg-transparent" />
      </div>
    );
  }

  if (error) {
    // caught by error boundary
    throw error;
  }

  if (!data?.length) {
    return <Alert.Info>Selection is not affected by wetlands.</Alert.Info>;
  }

  // Ignore the overarching category, this is not displayed in the summary
  const wetlandsCategories = data.filter((d) => d.sub_category !== "us_wetlands");

  return (
    <div className="flex flex-col gap-y-4">
      <DesignationAreaChart areas={wetlandsCategories} heading="Wetlands" />
    </div>
  );
}

HazardWetlandsLayerSummary.icon = "icon-lt-contrast-drop-2-line";
HazardWetlandsLayerSummary.supportedRegions = [SupportedRegions.us];
